import React, { useEffect, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';

import { getOrMakeUnitDefinitionFromMdsSiMarkup } from '@metservicedev/cf-units';
import { useMDSContext } from '@metservice/use-mds';
import { basicDataset } from '@mono/types/src/mds';
import { toHash } from '@mono/to-hash';

import { datasetToPoint, isDatasetAPoint, sourceId, generateSource, renderLayer, hoverLayer, selectedLayer } from './obs-mapbox-config';
import { SelectableList } from './selectable-list';
import { useSpaceConfiguratorContext } from 'space-configurator-context';

export const useObs = () => {
    const { state, enableObs, setState, map, enableGriddedDataset } = useSpaceConfiguratorContext();
    const { capabilities, theLongOne, findDatasets } = useMDSContext();
    const [datasets, setDatasets] = useState<basicDataset[]>();
    const [lastClick, setLastClick] = useState<mapboxgl.Point>();
    const [layersHaveBeenSetup, setLayersHaveBeenSetup] = useState(false);
    const [lastMoveEnd, setLastMoveEnd] = useState<[number, number, number, number] | undefined>();
    const [loading, setLoading] = useState(false);

    const constrainedCapabilities = useMemo(() => {
        if (!enableObs) return capabilities;
        if (enableObs === true) return capabilities;
        if (enableObs.constrainedCapabilities === undefined) return capabilities;

        return capabilities.filter((c) => {
            if (!enableObs.constrainedCapabilities) return true; //for typescript

            return enableObs.constrainedCapabilities.includes(c.id);
        });
    }, [toHash(enableObs), capabilities.map((c) => c.id).join()]);

    const filteredCapabilities = useMemo(() => {
        if (!enableGriddedDataset) return constrainedCapabilities.map((c) => c.id);
        if (!state.griddedCapabilityId) return;

        const forecastCapability = constrainedCapabilities.find((c) => c.id === state.griddedCapabilityId);
        if (!forecastCapability) return;

        const variable = Object.values(forecastCapability.variables)[0];
        if (!variable) return;

        const siUnit = getOrMakeUnitDefinitionFromMdsSiMarkup(variable.units);

        return constrainedCapabilities
            .filter((c) => {
                const variable = Object.values(c.variables)[0];
                if (!variable) return false;

                const consideringUnit = getOrMakeUnitDefinitionFromMdsSiMarkup(variable.units);
                if (!consideringUnit) return false;

                if (siUnit.unit !== consideringUnit.unit) return false;

                return true;
            })
            .map((c) => c.id);
    }, [state.griddedCapabilityId, constrainedCapabilities.map((c) => c.id).join()]);

    useEffect(() => {
        if (enableObs) return;

        setDatasets(undefined);
    }, [enableObs]);

    useEffect(() => {
        if (map === undefined) return;
        if (!enableObs) return;
        if (enableGriddedDataset && !state.griddedCapabilityId) {
            setDatasets(undefined);
            return;
        }

        const bounds = map.getBounds();
        const bbox: [number, number, number, number] = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];

        const abortController = new AbortController();
        setLoading(true);
        (async () => {
            try {
                if (!filteredCapabilities || filteredCapabilities.length === 0) {
                    const findDatasetsResponse = await findDatasets(
                        {
                            bbox,
                            temporal_nature: ['realtime'],
                            data_type: ['point'],
                        },
                        abortController.signal
                    );
                    const basicDatasets = findDatasetsResponse['basic-datasets'];
                    setDatasets(basicDatasets);
                    setLoading(false);
                    return;
                }

                const theLongOneResponse = await theLongOne(
                    {
                        capabilities: filteredCapabilities,
                        data_structure: 'timeseries/point2d',
                        mime_type: ['application/vnd.metocean.cf+json'],
                        show_objects: true,
                        bbox,
                        temporal_nature: 'realtime',
                        data_type: ['point'],
                    },
                    abortController.signal
                );

                setDatasets(theLongOneResponse.datasets);
                setLoading(false);
            } catch (error) {
                if (error.toString().includes('The user aborted a request')) return;
                throw error;
            }
        })();

        return () => {
            abortController.abort();
            setLoading(false);
        };
    }, [lastMoveEnd?.join(), filteredCapabilities?.join(), enableObs, state.griddedCapabilityId, map]);

    useEffect(() => {
        if (!enableObs) return;
        if (!map) return;
        if (!layersHaveBeenSetup) return;

        const onMoveEnd = async () => {
            const mapBounds = map.getBounds();
            const bbox: [number, number, number, number] = [mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()];
            setLastMoveEnd(bbox);
        };

        const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
            if (!map.getLayer(hoverLayer.id)) return;
            const features = map.queryRenderedFeatures(e.point, {
                layers: [hoverLayer.id],
            });
            const source = map.getSource(sourceId);
            if (!source) return;

            const hoveredIds = features.map((feature) => feature.id);
            const notHoveredIds = (source as any)._data.features.map((feature: any) => feature.id).filter((id: any) => !hoveredIds.includes(id));
            for (const id of hoveredIds) {
                map.setFeatureState({ source: sourceId, id }, { hover: true });
            }
            for (const id of notHoveredIds) {
                map.setFeatureState({ source: sourceId, id }, { hover: false });
            }
        };

        const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
            setLastClick(e.point);
        };

        const onZoomStart = () => {
            setLastClick(undefined);
        };

        map.on('moveend', onMoveEnd);
        map.on('mousemove', onMouseMove);
        map.on('mousedown', onMouseDown);
        map.on('zoomstart', onZoomStart);

        return () => {
            map.off('moveend', onMoveEnd);
            map.off('mousemove', onMouseMove);
            map.off('mousedown', onMouseDown);
            map.off('zoomstart', onZoomStart);
        };
    }, [map, layersHaveBeenSetup]);

    useEffect(() => {
        if (!map) return;
        if (!enableObs) return;

        const sourceData = generateSource(datasets ?? []);
        const existingSource = map.getSource(sourceId);
        if (existingSource && existingSource.type === 'geojson') {
            existingSource.setData(sourceData);
        }
        if (!existingSource) {
            map.addSource(sourceId, {
                type: 'geojson',
                promoteId: 'id',
                data: sourceData,
            });
        }

        const existingRenderLayer = map.getLayer(renderLayer.id);
        if (!existingRenderLayer) {
            map.addLayer(renderLayer);
        }

        const existingHoverLayer = map.getLayer(hoverLayer.id);
        if (!existingHoverLayer) {
            map.addLayer(hoverLayer);
        }

        const existingSelectedLayer = map.getLayer(selectedLayer.id);
        if (!existingSelectedLayer) {
            map.addLayer(selectedLayer);
        }

        setLayersHaveBeenSetup(true);
    }, [map, datasets?.length]);

    const ObsSelectList = useMemo(() => {
        if (!map) return; // if the map hasn't loaded yet, don't render the list
        if (!layersHaveBeenSetup) return; // if the layers haven't been setup yet, don't render the list
        if (!lastClick) return; // if the user hasn't clicked yet, don't render the list
        if (!enableObs) return; // if the user hasn't enabled obs, don't render the list
        if (!datasets) return; // if the obs datasets haven't loaded yet, don't render the list
        if (enableGriddedDataset && !state.griddedCapabilityId) return; // if the user hasn't selected a gridded capability, don't render the list

        const features = map.queryRenderedFeatures(lastClick, {
            layers: [hoverLayer.id],
        });
        if (features.length === 0) {
            return;
        }

        // If there is only one obs station that has been clicked, just select it - no need to show the list
        if (features.length === 1) {
            const id = features[0].id;
            if (typeof id !== 'string') return;
            const obsDataset = datasets.find((d) => d.id === id);
            if (!obsDataset) return;
            const coordinates = datasetToPoint(obsDataset);
            if (!coordinates) return;
            setState((prev) => ({ ...prev, obsId: id, obsCapabilityId: undefined, space: { type: 'Point', coordinates: [coordinates[0], coordinates[1]] } }));
            return;
        }

        return (
            <SelectableList
                items={features.map((feature) => {
                    const id = feature.id as string;
                    if (typeof id !== 'string') return { id };
                    const obsDataset = datasets.find((d) => d.id === id);
                    if (!obsDataset) return { id };
                    const coordinates = datasetToPoint(obsDataset);
                    if (!coordinates) return { id };
                    return {
                        id,
                        label: obsDataset.display_name_short,
                    };
                })}
                onClick={(id) => {
                    const obsDataset = datasets.find((d) => d.id === id);
                    if (!obsDataset) return;
                    const coordinates = datasetToPoint(obsDataset);
                    if (!coordinates) return;
                    setLastClick(undefined);
                    setState((prev) => ({
                        ...prev,
                        obsId: id,
                        obsCapabilityId: undefined,
                        space: { type: 'Point', coordinates: [coordinates[0], coordinates[1]] },
                    }));
                }}
                style={{ position: 'absolute', top: lastClick.y, left: lastClick.x }}
            />
        );
    }, [toHash(lastClick), layersHaveBeenSetup, datasets?.length]);

    useEffect(() => {
        if (!map) return;
        if (!layersHaveBeenSetup) return;
        if (!datasets) return;

        const features = map.getSource(sourceId);
        if (!features) return;
        if (features.type !== 'geojson') return;
        const pointDatasets = datasets.filter(isDatasetAPoint);

        for (const pointDataset of pointDatasets) {
            map.setFeatureState({ source: sourceId, id: pointDataset.id }, { isConsidering: false });
            if (pointDataset.id !== state.obsId) {
                map.setFeatureState({ source: sourceId, id: pointDataset.id }, { isSelected: false });
                continue;
            }
            map.setFeatureState({ source: sourceId, id: state.obsId }, { isSelected: true });
        }
    }, [toHash(datasets), state.obsId, layersHaveBeenSetup, datasets?.length]);

    return {
        ObsSelectList,
        datasets,
        loading,
    };
};
